import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  AnalyticReport,
  AnalyticReportDataRow,
  FieldDefinition
} from '../../types/analytic-reports/AnalyticReport';

type TabularReportProps = {
  report: AnalyticReport;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});
const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number) => currencyFormatter.format(Number.isFinite(value) ? value : 0);
const formatNumber = (value: number) => numberFormatter.format(Number.isFinite(value) ? value : 0);

const isObjectRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isDetailArray = (value: unknown): value is Record<string, unknown>[] => (
  Array.isArray(value) && value.every((item) => isObjectRecord(item))
);

const formatValue = (value: unknown, fieldType: FieldDefinition['fieldType']): string => {
  if (value === null || value === undefined || value === '') {
    return fieldType === 'number' || fieldType === 'currency' ? '-' : '';
  }
  if (fieldType === 'currency') return formatCurrency(Number(value));
  if (fieldType === 'number' || fieldType === 'percentage') return formatNumber(Number(value));
  return String(value);
};

const getCellAlign = (fieldType: FieldDefinition['fieldType']): 'left' | 'right' => {
  if (fieldType === 'number' || fieldType === 'currency' || fieldType === 'percentage') {
    return 'right';
  }
  return 'left';
};

const getDetailRows = (row: AnalyticReportDataRow): Record<string, unknown>[] => {
  return Object.values(row).flatMap((value) => (isDetailArray(value) ? value : []));
};

const TabularReport: React.FC<TabularReportProps> = ({ report }) => {
  const rows = (report.result?.data || []) as AnalyticReportDataRow[];
  const totals = (report.result?.totals || null) as AnalyticReportDataRow | null;
  const fieldDefinitions = report.fieldDefinitions || [];

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 'calc(100vh - 180px)',
        overflow: 'auto',
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          '& .MuiTableCell-root': {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontVariantNumeric: 'tabular-nums',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {fieldDefinitions.map((field) => (
              <TableCell
                key={field.name}
                align={getCellAlign(field.fieldType)}
                sx={{
                  backgroundColor: 'common.white',
                  zIndex: 2,
                }}
              >
                {field.label}
                {field.description && (
                  <Tooltip title={field.description} arrow>
                    <InfoOutlinedIcon
                      sx={{
                        ml: 0.5,
                        fontSize: '0.8rem',
                        color: 'text.disabled',
                        verticalAlign: 'middle',
                        cursor: 'help'
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const detailRows = getDetailRows(row);
            const rowKey = String(row.organizationKey || row._id || rowIndex);

            return (
              <React.Fragment key={rowKey}>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      fontSize: '0.95rem',
                      fontWeight: 500,
                    },
                  }}
                >
                  {fieldDefinitions.map((field) => (
                    <TableCell
                      key={field.name}
                      align={getCellAlign(field.fieldType)}
                      sx={{
                        color: field.name === 'margin' && Number(row[field.name]) < 0
                          ? 'error.main'
                          : 'inherit'
                      }}
                    >
                      {formatValue(row[field.name], field.fieldType)}
                    </TableCell>
                  ))}
                </TableRow>
                {detailRows.map((detailRow, detailIndex) => (
                  <TableRow
                    key={`${rowKey}-detail-${String(detailRow.electionKey || detailIndex)}`}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      '& .MuiTableCell-root': {
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                      },
                    }}
                  >
                    {fieldDefinitions.map((field, fieldIndex) => (
                      <TableCell
                        key={`${field.name}-${String(detailRow.electionKey || detailIndex)}`}
                        align={getCellAlign(field.fieldType)}
                        sx={fieldIndex === 0 ? { pl: 4, borderLeft: '3px solid', borderLeftColor: 'divider' } : undefined}
                      >
                        {formatValue(detailRow[field.name], field.fieldType)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}

          {totals && (
            <TableRow
              sx={{
                backgroundColor: 'common.white',
                borderTop: '2px solid',
                borderTopColor: 'divider',
                '& .MuiTableCell-root': {
                  fontWeight: 700,
                  fontSize: '0.9rem',
                },
              }}
            >
              {fieldDefinitions.map((field) => (
                <TableCell
                  key={`totals-${field.name}`}
                  align={getCellAlign(field.fieldType)}
                  sx={{
                    color: field.name === 'margin' && Number(totals[field.name]) < 0
                      ? 'error.main'
                      : 'inherit'
                  }}
                >
                  {formatValue(totals[field.name], field.fieldType)}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TabularReport;
